import React, { useState, useCallback, useEffect } from 'react';
import DocFullPage from './DocFullPage';
import DocSheet from './DocSheet';
import AtlasView from './Atlas';
import BrowsePopup from './Browse/BrowsePopup';
import { useDocs } from '../../hooks/useDocs';
import { useDocOrganization } from '../../hooks/useDocOrganization';
import { useAuth } from '../AuthProvider';
import { recordDocOpen } from '../../utils/docsHome';
import { useTabs } from '../../context/TabsContext';
import { useDocTab } from './useDocTab';
import { stackFromTab, useDocTabMemory } from './useDocTabMemory';
import './styles/index.css';

/**
 * DocArea — Atlas is the Documents home. Browse is a folder tool over Atlas,
 * not a peer view.
 *
 * Opening a doc shows it in the full-bleed sheet and puts it in the app tab
 * bar (one reusable doc tab while browsing). In-doc links push a back stack
 * stored on that tab, along with per-doc scroll. Closing the sheet returns
 * to the last view.
 */
const DocArea = () => {
  const { workspaceId } = useAuth();
  const { docs, createDoc, updateDoc, loading: docsLoading } = useDocs();
  const organization = useDocOrganization();
  const { undoOrganization, redoOrganization } = organization;
  const { showDocInTab, openDocInNewTab, syncActiveDocTab, returnToDocsHome } = useDocTab();
  const { activeTab } = useTabs();

  const [docStack, setDocStack] = useState(() => stackFromTab(activeTab));
  useDocTabMemory(activeTab, docStack, setDocStack);
  const stackDocId = docStack.length > 0 ? docStack[docStack.length - 1] : null;
  // Hide the sheet the same render the doc tab is closed or you leave it —
  // don't wait for a follow-up effect.
  const activeDocId =
    activeTab?.type === 'doc' && stackDocId ? stackDocId : null;

  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseFolderId, setBrowseFolderId] = useState(null);

  const closeBrowse = useCallback(() => {
    setBrowseOpen(false);
    setBrowseFolderId(null);
  }, []);

  useEffect(() => {
    closeBrowse();
  }, [workspaceId, closeBrowse]);

  useEffect(() => {
    if (activeTab?.type === 'docs') closeBrowse();
  }, [activeTab?.id, activeTab?.type, closeBrowse]);

  useEffect(() => {
    window.addEventListener('fokus:docs-home', closeBrowse);
    return () => window.removeEventListener('fokus:docs-home', closeBrowse);
  }, [closeBrowse]);

  const handleOpenDoc = useCallback(
    (docId) => {
      setDocStack((prev) =>
        prev[prev.length - 1] === docId ? prev : [...prev, docId]
      );
      recordDocOpen(workspaceId, docId);
      const doc = docs.find((d) => d.id === docId);
      showDocInTab(docId, doc?.title);
    },
    [workspaceId, docs, showDocInTab]
  );

  const handleOpenDocInNewTab = useCallback(
    (docId) => {
      if (!docId) return;
      recordDocOpen(workspaceId, docId);
      const doc = docs.find((d) => d.id === docId);
      openDocInNewTab(docId, doc?.title);
    },
    [workspaceId, docs, openDocInNewTab]
  );

  const handleBack = useCallback(() => {
    setDocStack((prev) => prev.slice(0, -1));
  }, []);

  const handleCloseSheet = useCallback(() => {
    setDocStack([]);
    returnToDocsHome();
  }, [returnToDocsHome]);

  const handleDismissMissingTab = useCallback(() => {
    setDocStack([]);
    returnToDocsHome();
  }, [returnToDocsHome]);

  useEffect(() => {
    const onOpenDoc = (e) => {
      const docId = e.detail?.docId;
      if (!docId) return;
      if (e.detail?.restore) {
        const restored = Array.isArray(e.detail.docStack) && e.detail.docStack.length
          ? e.detail.docStack
          : stackFromTab(activeTab);
        setDocStack(restored.length ? restored : [docId]);
        recordDocOpen(workspaceId, docId);
        return;
      }
      setDocStack([docId]);
      recordDocOpen(workspaceId, docId);
      const doc = docs.find((d) => d.id === docId);
      if (e.detail?.newTab) openDocInNewTab(docId, doc?.title);
      else showDocInTab(docId, doc?.title);
    };
    window.addEventListener('fokus:open-doc', onOpenDoc);
    return () => window.removeEventListener('fokus:open-doc', onOpenDoc);
  }, [workspaceId, docs, showDocInTab, openDocInNewTab, activeTab]);

  useEffect(() => {
    const onDocsChanged = (e) => {
      const { action, docId } = e.detail || {};
      if (action !== 'deleted' && action !== 'permanentlyDeleted') return;
      if (!docId) return;
      setDocStack((prev) => prev.filter((id) => id !== docId));
    };
    window.addEventListener('fokus:docs-changed', onDocsChanged);
    return () => window.removeEventListener('fokus:docs-changed', onDocsChanged);
  }, []);

  useEffect(() => {
    if (docStack.length <= 1) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setDocStack((prev) => prev.slice(0, -1));
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [docStack.length]);

  useEffect(() => {
    if (!activeDocId) return;
    const doc = docs.find((d) => d.id === activeDocId);
    if (!doc) return;
    syncActiveDocTab(activeDocId, doc.title);
  }, [activeDocId, docs, syncActiveDocTab]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (key !== 'z' && key !== 'y') return;
      if (activeDocId) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const redo = key === 'y' || (key === 'z' && e.shiftKey);
      const did = redo ? redoOrganization() : undoOrganization();
      if (did) e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeDocId, undoOrganization, redoOrganization]);

  const handleCreateDoc = useCallback(async () => {
    const doc = await createDoc({ title: 'Untitled Doc' });
    if (doc?.id) {
      setDocStack([doc.id]);
      recordDocOpen(workspaceId, doc.id);
      showDocInTab(doc.id, doc.title);
    }
    return doc;
  }, [createDoc, workspaceId, showDocInTab]);

  const handleBrowseFolder = useCallback((folderId) => {
    setBrowseFolderId(folderId || null);
    setBrowseOpen(true);
  }, []);

  const previousTitle =
    docStack.length > 1
      ? docs.find((d) => d.id === docStack[docStack.length - 2])?.title || 'Back'
      : 'Documents';

  return (
    <div className="doc-area">
      <div className="doc-area__stage">
        <div className="doc-area__scroll">
          <AtlasView
            organization={organization}
            docs={docs}
            docsLoading={docsLoading}
            onOpenDoc={handleOpenDoc}
            onOpenDocInNewTab={handleOpenDocInNewTab}
            onCreateDoc={handleCreateDoc}
            createDoc={createDoc}
            onBrowseFolder={handleBrowseFolder}
            useDemo={false}
          />
        </div>

        {browseOpen && (
          <BrowsePopup
            folderId={browseFolderId}
            organization={organization}
            docs={docs}
            onNavigateFolder={setBrowseFolderId}
            onOpenDoc={handleOpenDoc}
            onOpenDocInNewTab={handleOpenDocInNewTab}
            onCreateDoc={handleCreateDoc}
            createDoc={createDoc}
            onClose={closeBrowse}
          />
        )}
      </div>

      {activeDocId && (
        <DocSheet onRequestClose={handleCloseSheet}>
          {(closeSheet) => (
            <DocFullPage
              layout="sheet"
              docId={activeDocId}
              onBack={docStack.length > 1 ? handleBack : closeSheet}
              onClose={closeSheet}
              onDismiss={handleDismissMissingTab}
              backLabel={previousTitle}
              onSave={updateDoc}
              libraryDocs={docs}
              onOpenDoc={handleOpenDoc}
              organization={organization}
            />
          )}
        </DocSheet>
      )}
    </div>
  );
};

export default DocArea;
